import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/authContext';
import { Layout } from '../../components/Layout';
import { addNote, getCustomer, listNotes, updateCustomer, updateCustomerStatus } from './api';
export const CustomerDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    // Draft state for edit mode
    const [draft, setDraft] = useState({});
    // Notes
    const [notes, setNotes] = useState([]);
    const [notesLoading, setNotesLoading] = useState(false);
    const [noteBody, setNoteBody] = useState('');
    const [addingNote, setAddingNote] = useState(false);
    const [noteError, setNoteError] = useState(null);
    const loadCustomer = useCallback(async () => {
        if (!id)
            return;
        setLoading(true);
        setError(null);
        try {
            const c = await getCustomer(id);
            setCustomer(c);
        }
        catch (e) {
            setError(e.message ?? 'Failed to load customer');
        }
        finally {
            setLoading(false);
        }
    }, [id]);
    const loadNotes = useCallback(async () => {
        if (!id)
            return;
        setNotesLoading(true);
        try {
            const result = await listNotes(id, { limit: 50 });
            // newest-first: sort by timestamp descending
            const sorted = [...result.items].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
            setNotes(sorted);
        }
        catch {
            // non-critical; silently fail
        }
        finally {
            setNotesLoading(false);
        }
    }, [id]);
    useEffect(() => {
        loadCustomer();
        loadNotes();
    }, [loadCustomer, loadNotes]);
    const startEdit = () => {
        if (!customer)
            return;
        setDraft({
            name: customer.name,
            type: customer.type,
            primaryContact: { ...customer.primaryContact },
            email: customer.email ?? '',
            phones: customer.phones.map((p) => ({ ...p })),
            addresses: customer.addresses.map((a) => ({ ...a })),
        });
        setSaveError(null);
        setEditing(true);
    };
    const cancelEdit = () => {
        setEditing(false);
        setDraft({});
        setSaveError(null);
    };
    const handleSave = async () => {
        if (!customer || !id)
            return;
        setSaving(true);
        setSaveError(null);
        try {
            const updated = await updateCustomer(id, draft);
            setCustomer(updated);
            setEditing(false);
            setDraft({});
        }
        catch (e) {
            setSaveError(e.message ?? 'Failed to save');
        }
        finally {
            setSaving(false);
        }
    };
    const handleToggleStatus = async () => {
        if (!customer || !id)
            return;
        const newStatus = customer.status === 'active' ? 'inactive' : 'active';
        try {
            const updated = await updateCustomerStatus(id, newStatus);
            setCustomer(updated);
        }
        catch (e) {
            setError(e.message ?? 'Failed to update status');
        }
    };
    const handleAddNote = async () => {
        if (!id || !noteBody.trim())
            return;
        setAddingNote(true);
        setNoteError(null);
        try {
            await addNote(id, { author: user?.username ?? 'unknown', body: noteBody.trim() });
            setNoteBody('');
            await loadNotes();
        }
        catch (e) {
            setNoteError(e.message ?? 'Failed to add note');
        }
        finally {
            setAddingNote(false);
        }
    };
    // Draft helpers
    const updateDraftAddress = (idx, field, value) => {
        setDraft((prev) => {
            const addresses = [...(prev.addresses ?? [])];
            addresses[idx] = { ...addresses[idx], [field]: value };
            return { ...prev, addresses };
        });
    };
    const addDraftAddress = () => {
        setDraft((prev) => ({
            ...prev,
            addresses: [...(prev.addresses ?? []), { label: '', street: '', city: '', state: '', zip: '' }],
        }));
    };
    const removeDraftAddress = (idx) => {
        setDraft((prev) => {
            const addresses = (prev.addresses ?? []).filter((_, i) => i !== idx);
            return { ...prev, addresses };
        });
    };
    const updateDraftPhone = (idx, field, value) => {
        setDraft((prev) => {
            const phones = [...(prev.phones ?? [])];
            phones[idx] = { ...phones[idx], [field]: value };
            return { ...prev, phones };
        });
    };
    const addDraftPhone = () => {
        setDraft((prev) => ({
            ...prev,
            phones: [...(prev.phones ?? []), { type: 'mobile', number: '' }],
        }));
    };
    const removeDraftPhone = (idx) => {
        setDraft((prev) => {
            const phones = (prev.phones ?? []).filter((_, i) => i !== idx);
            return { ...prev, phones };
        });
    };
    if (loading) {
        return (_jsx(Layout, { children: _jsx("div", { style: styles.loadingCenter, children: _jsx("div", { style: styles.spinner }) }) }));
    }
    if (error || !customer) {
        return (_jsxs(Layout, { children: [_jsx("div", { style: styles.errorBanner, children: error ?? 'Customer not found' }), _jsx("button", { style: styles.backLink, onClick: () => navigate('/customers'), children: "\u2190 Back to Customers" })] }));
    }
    const displayData = editing ? draft : customer;
    return (_jsx(Layout, { children: _jsxs("div", { children: [_jsx("button", { style: styles.backLink, onClick: () => navigate('/customers'), children: "\u2190 Customers" }), _jsxs("div", { style: styles.card, children: [_jsxs("div", { style: styles.cardHeaderRow, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }, children: [_jsx("h2", { style: styles.customerName, children: customer.name }), _jsx("span", { style: customer.status === 'active' ? styles.badgeGreen : styles.badgeGray, children: customer.status === 'active' ? 'Active' : 'Inactive' }), _jsx("span", { style: customer.type === 'commercial' ? styles.badgeBlue : styles.badgeTeal, children: customer.type === 'commercial' ? 'Commercial' : 'Residential' })] }), _jsx("div", { style: { display: 'flex', gap: '10px', flexWrap: 'wrap' }, children: !editing ? (_jsx("button", { style: styles.secondaryButton, onClick: startEdit, children: "Edit" })) : (_jsxs(_Fragment, { children: [_jsx("button", { style: styles.ghostButton, onClick: cancelEdit, disabled: saving, children: "Cancel" }), _jsx("button", { style: styles.primaryButton, onClick: handleSave, disabled: saving, children: saving ? 'Saving…' : 'Save' })] })) })] }), saveError && _jsx("div", { style: { ...styles.errorBanner, marginTop: '12px' }, children: saveError })] }), _jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.sectionTitle, children: "Customer Info" }), _jsxs("div", { style: styles.fieldGrid, children: [_jsx(Field, { label: "Name", editing: editing, value: editing ? (draft.name ?? '') : customer.name, onChange: (v) => setDraft((p) => ({ ...p, name: v })) }), _jsxs("div", { style: styles.fieldGroup, children: [_jsx("label", { style: styles.fieldLabel, children: "Type" }), editing ? (_jsxs("select", { style: styles.input, value: draft.type ?? customer.type, onChange: (e) => setDraft((p) => ({ ...p, type: e.target.value })), children: [_jsx("option", { value: "commercial", children: "Commercial" }), _jsx("option", { value: "residential", children: "Residential" })] })) : (_jsx("p", { style: styles.fieldValue, children: customer.type }))] }), _jsx(Field, { label: "Email", editing: editing, value: editing ? (draft.email ?? '') : (customer.email ?? '—'), onChange: (v) => setDraft((p) => ({ ...p, email: v })), type: "email" }), _jsx(Field, { label: "First Name", editing: editing, value: editing
                                        ? (draft.primaryContact?.firstName ?? '')
                                        : customer.primaryContact.firstName, onChange: (v) => setDraft((p) => ({
                                        ...p,
                                        primaryContact: { ...(p.primaryContact ?? customer.primaryContact), firstName: v },
                                    })) }), _jsx(Field, { label: "Last Name", editing: editing, value: editing ? (draft.primaryContact?.lastName ?? '') : customer.primaryContact.lastName, onChange: (v) => setDraft((p) => ({
                                        ...p,
                                        primaryContact: { ...(p.primaryContact ?? customer.primaryContact), lastName: v },
                                    })) }), _jsxs("div", { style: styles.fieldGroup, children: [_jsx("label", { style: styles.fieldLabel, children: "Status" }), editing ? (_jsx("p", { style: styles.fieldValue, children: "(Use button below)" })) : (_jsx("button", { style: customer.status === 'active' ? styles.dangerButton : styles.successButton, onClick: handleToggleStatus, children: customer.status === 'active' ? 'Deactivate' : 'Activate' }))] })] })] }), _jsxs("div", { style: styles.card, children: [_jsxs("div", { style: styles.sectionHeaderRow, children: [_jsx("h3", { style: styles.sectionTitle, children: "Addresses" }), editing && (_jsx("button", { style: styles.addItemButton, onClick: addDraftAddress, children: "+ Add Address" }))] }), (editing ? draft.addresses ?? [] : customer.addresses).map((addr, idx) => (_jsx("div", { style: styles.subCard, children: editing ? (_jsxs("div", { children: [_jsxs("div", { style: styles.fieldRow, children: [_jsx("input", { style: styles.input, placeholder: "Label (e.g. Main Office)", value: addr.label, onChange: (e) => updateDraftAddress(idx, 'label', e.target.value) }), (draft.addresses?.length ?? 0) > 1 && (_jsx("button", { style: styles.removeButton, onClick: () => removeDraftAddress(idx), children: "Remove" }))] }), _jsx("input", { style: { ...styles.input, marginTop: '8px' }, placeholder: "Street", value: addr.street, onChange: (e) => updateDraftAddress(idx, 'street', e.target.value) }), _jsxs("div", { style: { ...styles.fieldRow, marginTop: '8px' }, children: [_jsx("input", { style: styles.input, placeholder: "City", value: addr.city, onChange: (e) => updateDraftAddress(idx, 'city', e.target.value) }), _jsx("input", { style: { ...styles.input, maxWidth: '80px' }, placeholder: "State", value: addr.state, onChange: (e) => updateDraftAddress(idx, 'state', e.target.value) }), _jsx("input", { style: { ...styles.input, maxWidth: '100px' }, placeholder: "ZIP", value: addr.zip, onChange: (e) => updateDraftAddress(idx, 'zip', e.target.value) })] })] })) : (_jsxs("div", { children: [_jsx("p", { style: styles.addrLabel, children: addr.label }), _jsx("p", { style: styles.addrLine, children: addr.street }), _jsxs("p", { style: styles.addrLine, children: [addr.city, ", ", addr.state, " ", addr.zip] })] })) }, idx)))] }), _jsxs("div", { style: styles.card, children: [_jsxs("div", { style: styles.sectionHeaderRow, children: [_jsx("h3", { style: styles.sectionTitle, children: "Phone Numbers" }), editing && (_jsx("button", { style: styles.addItemButton, onClick: addDraftPhone, children: "+ Add Phone" }))] }), (editing ? draft.phones ?? [] : customer.phones).length === 0 && (_jsx("p", { style: styles.emptySubText, children: "No phone numbers on file." })), (editing ? draft.phones ?? [] : customer.phones).map((phone, idx) => (_jsx("div", { style: styles.phoneRow, children: editing ? (_jsxs(_Fragment, { children: [_jsxs("select", { style: { ...styles.input, maxWidth: '120px' }, value: phone.type, onChange: (e) => updateDraftPhone(idx, 'type', e.target.value), children: [_jsx("option", { value: "mobile", children: "Mobile" }), _jsx("option", { value: "office", children: "Office" }), _jsx("option", { value: "fax", children: "Fax" }), _jsx("option", { value: "other", children: "Other" })] }), _jsx("input", { style: styles.input, placeholder: "Phone number", value: phone.number, onChange: (e) => updateDraftPhone(idx, 'number', e.target.value) }), _jsx("button", { style: styles.removeButton, onClick: () => removeDraftPhone(idx), children: "Remove" })] })) : (_jsxs(_Fragment, { children: [_jsx("span", { style: styles.phoneBadge, children: phone.type }), _jsx("span", { style: styles.phoneNumber, children: phone.number })] })) }, idx)))] }), _jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.sectionTitle, children: "Notes" }), _jsxs("div", { style: styles.noteInputArea, children: [_jsx("textarea", { style: styles.noteTextarea, placeholder: "Add a note\u2026", value: noteBody, onChange: (e) => setNoteBody(e.target.value), rows: 3 }), noteError && _jsx("div", { style: { ...styles.errorBanner, marginTop: '8px' }, children: noteError }), _jsx("button", { style: styles.primaryButton, onClick: handleAddNote, disabled: addingNote || !noteBody.trim(), children: addingNote ? 'Adding…' : 'Add Note' })] }), notesLoading && (_jsx("div", { style: styles.loadingCenter, children: _jsx("div", { style: styles.spinner }) })), !notesLoading && notes.length === 0 && (_jsx("p", { style: styles.emptySubText, children: "No notes yet." })), notes.map((note, idx) => (_jsxs("div", { style: styles.noteItem, children: [_jsxs("div", { style: styles.noteMeta, children: [_jsx("span", { style: styles.noteAuthor, children: note.author }), _jsx("span", { style: styles.noteTimestamp, children: new Date(note.timestamp).toLocaleString() })] }), _jsx("p", { style: styles.noteBody, children: note.body })] }, idx)))] })] }) }));
};
const Field = ({ label, editing, value, onChange, type = 'text' }) => (_jsxs("div", { style: styles.fieldGroup, children: [_jsx("label", { style: styles.fieldLabel, children: label }), editing ? (_jsx("input", { style: styles.input, type: type, value: value, onChange: (e) => onChange(e.target.value) })) : (_jsx("p", { style: styles.fieldValue, children: value || '—' }))] }));
const styles = {
    loadingCenter: {
        display: 'flex',
        justifyContent: 'center',
        padding: '80px 0',
    },
    spinner: {
        width: '32px',
        height: '32px',
        border: '3px solid rgba(0,0,0,0.1)',
        borderTopColor: '#0071e3',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    backLink: {
        background: 'none',
        border: 'none',
        color: '#0071e3',
        fontSize: '15px',
        fontWeight: '500',
        cursor: 'pointer',
        padding: '0 0 20px 0',
        display: 'block',
    },
    card: {
        padding: '28px 32px',
        backgroundColor: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        marginBottom: '20px',
    },
    cardHeaderRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px',
    },
    customerName: {
        fontSize: '32px',
        fontWeight: '700',
        margin: 0,
        color: '#1d1d1f',
        letterSpacing: '-0.5px',
    },
    sectionTitle: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#1d1d1f',
        margin: '0 0 20px 0',
        letterSpacing: '-0.3px',
    },
    sectionHeaderRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px',
    },
    fieldGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '16px 24px',
    },
    fieldGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
    },
    fieldLabel: {
        fontSize: '12px',
        fontWeight: '600',
        color: '#86868b',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    fieldValue: {
        margin: 0,
        fontSize: '15px',
        color: '#1d1d1f',
        fontWeight: '400',
    },
    fieldRow: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
    },
    input: {
        flex: 1,
        padding: '9px 12px',
        fontSize: '15px',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        color: '#1d1d1f',
        outline: 'none',
        minWidth: 0,
    },
    subCard: {
        padding: '16px',
        backgroundColor: 'rgba(0,0,0,0.025)',
        borderRadius: '12px',
        marginBottom: '12px',
    },
    addrLabel: {
        margin: '0 0 4px 0',
        fontSize: '13px',
        fontWeight: '600',
        color: '#86868b',
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
    },
    addrLine: {
        margin: '0 0 2px 0',
        fontSize: '15px',
        color: '#1d1d1f',
    },
    phoneRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '10px',
    },
    phoneBadge: {
        padding: '3px 10px',
        backgroundColor: 'rgba(142,142,147,0.15)',
        color: '#86868b',
        borderRadius: '980px',
        fontSize: '12px',
        fontWeight: '600',
        textTransform: 'capitalize',
    },
    phoneNumber: {
        fontSize: '15px',
        color: '#1d1d1f',
    },
    noteInputArea: {
        marginBottom: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    noteTextarea: {
        width: '100%',
        padding: '12px',
        fontSize: '15px',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '12px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        color: '#1d1d1f',
        outline: 'none',
        resize: 'vertical',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
    },
    noteItem: {
        padding: '16px',
        backgroundColor: 'rgba(0,0,0,0.025)',
        borderRadius: '12px',
        marginBottom: '10px',
    },
    noteMeta: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
    },
    noteAuthor: {
        fontSize: '13px',
        fontWeight: '700',
        color: '#1d1d1f',
    },
    noteTimestamp: {
        fontSize: '12px',
        color: '#86868b',
    },
    noteBody: {
        margin: 0,
        fontSize: '15px',
        color: '#3c3c43',
        lineHeight: '1.5',
        whiteSpace: 'pre-wrap',
    },
    emptySubText: {
        color: '#86868b',
        fontSize: '14px',
        margin: 0,
    },
    primaryButton: {
        alignSelf: 'flex-start',
        padding: '10px 22px',
        backgroundColor: '#0071e3',
        color: '#fff',
        border: 'none',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    secondaryButton: {
        padding: '10px 22px',
        backgroundColor: 'rgba(0,0,0,0.06)',
        color: '#1d1d1f',
        border: 'none',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    ghostButton: {
        padding: '10px 22px',
        backgroundColor: 'transparent',
        color: '#86868b',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer',
    },
    dangerButton: {
        alignSelf: 'flex-start',
        padding: '8px 16px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        color: '#c0392b',
        border: 'none',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    successButton: {
        alignSelf: 'flex-start',
        padding: '8px 16px',
        backgroundColor: 'rgba(52,199,89,0.12)',
        color: '#248a3d',
        border: 'none',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    addItemButton: {
        padding: '7px 16px',
        backgroundColor: 'transparent',
        color: '#0071e3',
        border: '1px solid #0071e3',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    removeButton: {
        padding: '7px 14px',
        backgroundColor: 'transparent',
        color: '#c0392b',
        border: '1px solid rgba(255,59,48,0.4)',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '500',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    errorBanner: {
        padding: '12px 16px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        border: '1px solid rgba(255,59,48,0.3)',
        borderRadius: '10px',
        color: '#c0392b',
        fontSize: '14px',
    },
    badgeGreen: {
        display: 'inline-block',
        padding: '4px 12px',
        backgroundColor: 'rgba(52,199,89,0.12)',
        color: '#248a3d',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
    },
    badgeGray: {
        display: 'inline-block',
        padding: '4px 12px',
        backgroundColor: 'rgba(142,142,147,0.15)',
        color: '#86868b',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
    },
    badgeBlue: {
        display: 'inline-block',
        padding: '4px 12px',
        backgroundColor: 'rgba(0,122,255,0.12)',
        color: '#0071e3',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
    },
    badgeTeal: {
        display: 'inline-block',
        padding: '4px 12px',
        backgroundColor: 'rgba(48,209,88,0.12)',
        color: '#1a7f37',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
    },
};
//# sourceMappingURL=CustomerDetail.js.map